import * as React from "react";
import { AlertTriangle, Info, Trash2, HelpCircle } from "lucide-react";
import { cn } from "lib/utils";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { IconTile } from "components/ui/icon-tile";
import { Field } from "components/ui/field";
import type { Tone } from "components/ui/tone";

/* ══════════════════════════════════════════════════════════════════════════
   ConfirmDialog — the app's only confirmation surface.

   There were three: `ui/confirmationDialog.tsx` (Radix), plus hand-built
   portal dialogs in `support/` and `templates/`. The two hand-built ones drew
   their own backdrop, had no focus trap or Escape handling, and used
   light-only colours (`bg-surface`, `text-content`) so they rendered as white
   rectangles in dark mode.

   This one supersedes all three and keeps the union of their features,
   including the `support/` variant's type-to-confirm gate for irreversible
   actions. The originals now re-export adapters over this, so no call site
   had to change.
   ══════════════════════════════════════════════════════════════════════════ */

export type ConfirmTone = Extract<Tone, "danger" | "warning" | "info" | "brand">;

const TONE_ICON: Record<ConfirmTone, React.ElementType> = {
    danger: Trash2,
    warning: AlertTriangle,
    info: Info,
    brand: HelpCircle,
};

export interface ConfirmDialogProps {
    open: boolean;
    title: React.ReactNode;
    /** Body copy — say what will happen, not just "are you sure" */
    description?: React.ReactNode;
    tone?: ConfirmTone;
    icon?: React.ElementType;
    showIcon?: boolean;

    confirmLabel?: string;
    cancelLabel?: string;

    isLoading?: boolean;
    /** Blocks confirm for reasons the caller owns */
    disabled?: boolean;

    /**
     * Requires the user to type `confirmationText` before confirm unlocks.
     * Reserve it for actions with no undo.
     */
    requireConfirmation?: boolean;
    confirmationText?: string;

    /** Extra content between the description and the actions */
    children?: React.ReactNode;

    onConfirm: () => void;
    onCancel: () => void;
    className?: string;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    title,
    description,
    tone = "danger",
    icon,
    showIcon = true,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    isLoading = false,
    disabled = false,
    requireConfirmation = false,
    confirmationText = "",
    children,
    onConfirm,
    onCancel,
    className,
}) => {
    const [typed, setTyped] = React.useState("");

    React.useEffect(() => {
        if (open) setTyped("");
    }, [open]);

    const gateSatisfied = !requireConfirmation || typed.trim() === confirmationText.trim();
    const confirmDisabled = disabled || isLoading || !gateSatisfied;

    const Icon = icon ?? TONE_ICON[tone];

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next && !isLoading) onCancel();
            }}
        >
            <DialogContent
                size="sm"
                showClose={false}
                padding={false}
                className={cn("sm:max-w-[28rem]", className)}
                onEscapeKeyDown={(e) => {
                    if (isLoading) e.preventDefault();
                }}
            >
                <div className="p-5 sm:p-6">
                    <div className="flex items-start gap-3.5">
                        {showIcon && (
                            <IconTile tone={tone} size="lg">
                                <Icon strokeWidth={2} />
                            </IconTile>
                        )}
                        <div className="min-w-0 flex-1 pt-0.5">
                            <DialogTitle className="text-title-sm text-content">{title}</DialogTitle>
                            {description && (
                                <DialogDescription className="mt-1.5 text-ui text-content-muted leading-relaxed">
                                    {description}
                                </DialogDescription>
                            )}
                        </div>
                    </div>

                    {children && <div className="mt-4">{children}</div>}

                    {requireConfirmation && confirmationText && (
                        <div className="mt-4">
                            <Field
                                label={
                                    <>
                                        Type <span className="font-mono font-bold text-content">{confirmationText}</span> to
                                        confirm
                                    </>
                                }
                            >
                                {(control) => (
                                    <Input
                                        {...control}
                                        value={typed}
                                        onChange={(e) => setTyped(e.target.value)}
                                        placeholder={confirmationText}
                                        autoComplete="off"
                                        spellCheck={false}
                                        disabled={isLoading}
                                    />
                                )}
                            </Field>
                        </div>
                    )}

                    <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
                            {cancelLabel}
                        </Button>
                        <Button
                            autoFocus={!requireConfirmation}
                            variant={tone === "danger" ? "destructive" : "primary"}
                            onClick={onConfirm}
                            disabled={confirmDisabled}
                            loading={isLoading}
                        >
                            {confirmLabel}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ConfirmDialog;
